import { useState, useEffect } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { X, Volume2, VolumeX, Sparkles, Eye, EyeOff, Cloud, LogOut, Calendar, Palette } from "lucide-react";
import { useSoundSettings } from "@/components/app/SoundManager";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme, THEMES } from "@/hooks/useTheme";
import { NudgeEmailSection } from "@/components/app/settings/NudgeEmailSection";
import { LockScreenNudgeRow } from "@/components/app/settings/LockScreenNudgeRow";

export const SettingsPanel = ({ isOpen, onClose }) => {
  const { soundEnabled, toggleSound, playProgress } = useSoundSettings();
  const { user, isAuthenticated, signIn, signOut, saveUserData } = useAuth();
  const { theme, setTheme } = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Swipe-down-to-dismiss (iOS native feel) — dragging is scoped to the
  // grab handle only, so tapping theme chips or toggles below doesn't
  // accidentally trigger a drag or steal the click.
  const y = useMotionValue(0);
  const handleDragEnd = (_, info) => {
    if (info.offset.y > 120 || info.velocity.y > 500) {
      // Snap the modal off-screen then fire the close so the exit animation
      // doesn't fight the drag.
      animate(y, 800, { duration: 0.2 });
      setTimeout(onClose, 180);
    } else {
      animate(y, 0, { type: "spring", stiffness: 300, damping: 30 });
    }
  };
  
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('wwi_animations');
      if (stored !== null) return stored === 'true';
      // Check for reduced motion preference
      return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return true;
  });

  const [celebrationsEnabled, setCelebrationsEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('wwi_celebrations') !== 'false';
    }
    return true;
  });

  const [dailyReminderEnabled, setDailyReminderEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      // Default to ON if not set
      const stored = localStorage.getItem('wwi_daily_reminder');
      return stored !== 'false';
    }
    return true;
  });

  // Nudge email preference + Lock Screen Nudges toggle are extracted into
  // dedicated subcomponents (see components/app/settings/*). This file now
  // just orchestrates the modal and general settings toggles.

  useEffect(() => {
    localStorage.setItem('wwi_animations', animationsEnabled.toString());
  }, [animationsEnabled]);

  useEffect(() => {
    localStorage.setItem('wwi_celebrations', celebrationsEnabled.toString());
  }, [celebrationsEnabled]);

  useEffect(() => {
    localStorage.setItem('wwi_daily_reminder', dailyReminderEnabled.toString());
  }, [dailyReminderEnabled]);

  const handleSoundToggle = () => {
    toggleSound();
    // Play a sample sound when enabling
    if (!soundEnabled) {
      setTimeout(() => playProgress(), 100);
    }
  };

  const handleSaveToCloud = async () => {
    if (!isAuthenticated) {
      signIn();
      return;
    }
    
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Get data from localStorage
      const boardData = JSON.parse(localStorage.getItem('wherewasi_data') || '{}');
      const fireCounter = JSON.parse(localStorage.getItem('wwi_fire_counter') || '{}');
      const settings = {
        sounds: soundEnabled,
        animations: animationsEnabled,
        celebrations: celebrationsEnabled
      };
      
      const success = await saveUserData(boardData, fireCounter, settings);
      
      if (success) {
        setSaveMessage('Saved!');
        setTimeout(() => setSaveMessage(''), 2000);
      } else {
        setSaveMessage('Failed to save');
      }
    } catch (error) {
      setSaveMessage('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 flex items-start sm:items-center justify-center z-[60] p-3 sm:p-4 overflow-y-auto"
      style={{
        paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0))',
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0))',
      }}
      initial={{ backgroundColor: 'rgba(0, 0, 0, 0)' }}
      animate={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
      exit={{ backgroundColor: 'rgba(0, 0, 0, 0)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        style={{ y }}
        className="bg-white rounded-2xl w-full max-w-sm shadow-xl flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] overflow-hidden mx-auto"
      >
        {/* Grab handle — visual affordance that the sheet can be swiped
            down. Drag is scoped to this handle only so taps on interior
            controls (theme chips, toggles, buttons) never race with a
            drag intent. */}
        <motion.div
          drag="y"
          dragConstraints={{ top: 0, bottom: 800 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          onDragEnd={handleDragEnd}
          onDrag={(_, info) => y.set(Math.max(0, info.offset.y))}
          data-testid="settings-drag-handle"
          className="flex justify-center pt-3 pb-2 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="w-10 h-1.5 rounded-full bg-stone-300/70" />
        </motion.div>

        {/* Sticky header with close-X — always reachable on iOS even when
            the body content is scrolled. Includes a thumb-friendly close-row
            below the X so users can dismiss with the bottom of the screen. */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-stone-100 bg-white flex-shrink-0">
          <h2 className="text-lg font-semibold text-[#27272A]">Settings</h2>
          <button
            onClick={onClose}
            data-testid="settings-close-button"
            className="p-2 -mr-2 text-stone-400 hover:text-stone-600 transition-colors"
            aria-label="Close settings"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Sound toggle */}
          <div className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
            <div className="flex items-center gap-3">
              {soundEnabled ? (
                <Volume2 size={20} className="text-[#3DC4D0]" />
              ) : (
                <VolumeX size={20} className="text-stone-400" />
              )}
              <div>
                <p className="font-medium text-[#27272A]">Momentum sounds</p>
                <p className="text-xs text-[#A1A1AA]">Subtle audio feedback</p>
              </div>
            </div>
            <button
              onClick={handleSoundToggle}
              data-testid="sounds-toggle"
              className={`w-12 h-7 rounded-full transition-colors ${
                soundEnabled ? "bg-[#3DC4D0]" : "bg-stone-300"
              }`}
            >
              <motion.div
                className="w-5 h-5 bg-white rounded-full shadow-sm"
                animate={{ x: soundEnabled ? 22 : 4 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
          </div>

          {/* Theme picker — compact inline row (matches other setting rows) */}
          <div className="p-4 bg-[#FDFBF7] rounded-xl space-y-2" data-testid="theme-picker">
            <div className="flex items-center gap-3">
              <Palette size={20} className="text-[#F58227]" />
              <div>
                <p className="font-medium text-[#27272A]">Color theme</p>
                <p className="text-xs text-[#A1A1AA]">How the app looks</p>
              </div>
            </div>
            <div className="flex gap-1.5" role="radiogroup" aria-label="Color theme">
              {THEMES.map((t) => {
                const active = theme === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTheme(t.key)}
                    className={`flex-1 relative rounded-lg py-1.5 px-1 border transition-all flex flex-col items-center gap-1 ${
                      active
                        ? 'border-[#F58227] bg-white shadow-sm'
                        : 'border-stone-200 bg-white/60 hover:border-stone-300'
                    }`}
                    data-testid={`theme-btn-${t.key}`}
                    aria-pressed={active}
                    role="radio"
                    aria-checked={active}
                    title={t.description}
                  >
                    <div className="flex gap-[2px] rounded-sm overflow-hidden h-3 w-full">
                      {t.swatches.map((c, i) => (
                        <div key={i} className="flex-1" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <span className={`text-[10px] leading-none font-medium ${active ? 'text-[#F58227]' : 'text-[#52525B]'}`}>
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Animations toggle */}
          <div className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
            <div className="flex items-center gap-3">
              {animationsEnabled ? (
                <Eye size={20} className="text-[#3DC4D0]" />
              ) : (
                <EyeOff size={20} className="text-stone-400" />
              )}
              <div>
                <p className="font-medium text-[#27272A]">Animations</p>
                <p className="text-xs text-[#A1A1AA]">Visual transitions</p>
              </div>
            </div>
            <button
              onClick={() => setAnimationsEnabled(prev => !prev)}
              data-testid="animations-toggle"
              className={`w-12 h-7 rounded-full transition-colors ${
                animationsEnabled ? "bg-[#3DC4D0]" : "bg-stone-300"
              }`}
            >
              <motion.div
                className="w-5 h-5 bg-white rounded-full shadow-sm"
                animate={{ x: animationsEnabled ? 22 : 4 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
          </div>

          {/* Celebrations toggle */}
          <div className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
            <div className="flex items-center gap-3">
              <Sparkles size={20} className={celebrationsEnabled ? "text-[#F7941D]" : "text-stone-400"} />
              <div>
                <p className="font-medium text-[#27272A]">Celebrations</p>
                <p className="text-xs text-[#A1A1AA]">Completion feedback</p>
              </div>
            </div>
            <button
              onClick={() => setCelebrationsEnabled(prev => !prev)}
              data-testid="celebrations-toggle"
              className={`w-12 h-7 rounded-full transition-colors ${
                celebrationsEnabled ? "bg-[#F7941D]" : "bg-stone-300"
              }`}
            >
              <motion.div
                className="w-5 h-5 bg-white rounded-full shadow-sm"
                animate={{ x: celebrationsEnabled ? 22 : 4 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
          </div>

          {/* Daily calendar reminder toggle */}
          <div className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
            <div className="flex items-center gap-3">
              <Calendar size={20} className={dailyReminderEnabled ? "text-[#3DC4D0]" : "text-stone-400"} />
              <div>
                <p className="font-medium text-[#27272A]">Daily reminder</p>
                <p className="text-xs text-[#A1A1AA]">Check your calendar</p>
              </div>
            </div>
            <button
              onClick={() => setDailyReminderEnabled(prev => !prev)}
              data-testid="daily-reminder-toggle"
              className={`w-12 h-7 rounded-full transition-colors ${
                dailyReminderEnabled ? "bg-[#3DC4D0]" : "bg-stone-300"
              }`}
            >
              <motion.div
                className="w-5 h-5 bg-white rounded-full shadow-sm"
                animate={{ x: dailyReminderEnabled ? 22 : 4 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
          </div>

          {/* Lock Screen Nudges (global On/Off) — extracted subcomponent */}
          <LockScreenNudgeRow isAuthenticated={isAuthenticated} onClose={onClose} />

        {/* Security Note */}
        <div className="mt-6 pt-4 border-t border-stone-100">
          <p className="text-xs text-[#A1A1AA] text-center">
            For your safety, don't store passwords or sensitive credentials here.
          </p>
        </div>
        <div className="mt-6 pt-6 border-t border-stone-100">
          <h3 className="text-sm font-medium text-[#27272A] mb-4">Keep Your Thread</h3>
          {isAuthenticated ? (
            <div className="space-y-4">
              {/* User info */}
              <div className="flex items-center gap-3 p-4 bg-[#FDFBF7] rounded-xl">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt="" 
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#3DC4D0] flex items-center justify-center text-white font-medium">
                    {user?.name?.[0] || 'U'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#27272A] truncate">{user?.name}</p>
                  <p className="text-xs text-[#A1A1AA] truncate">{user?.email}</p>
                </div>
              </div>

              {/* Nudge email preference — extracted subcomponent. Reuses
                  reminder_preferences.email so users can route nudge emails
                  to a different address than their Google sign-in account. */}
              <NudgeEmailSection isOpen={isOpen} isAuthenticated={isAuthenticated} user={user} />
              
              {/* Sync status message */}
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                <Cloud size={14} className="text-green-600" />
                <p className="text-xs text-green-700">Your threads are being saved automatically</p>
              </div>
              
              {/* Manual save button */}
              <button
                onClick={handleSaveToCloud}
                disabled={isSaving}
                data-testid="save-to-cloud-btn"
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#FDFBF7] text-[#27272A] rounded-xl font-medium hover:bg-stone-100 transition-colors disabled:opacity-50 border border-stone-200"
              >
                <Cloud size={18} className="text-[#3DC4D0]" />
                {isSaving ? 'Saving...' : saveMessage || 'Sync now'}
              </button>
              
              {/* Sign out */}
              <button
                onClick={signOut}
                className="w-full flex items-center justify-center gap-2 py-2 text-stone-500 hover:text-stone-700 transition-colors"
              >
                <LogOut size={16} />
                <span className="text-sm">Sign out (data stays on device)</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#52525B]">
                Continue where you left off, on any device.
              </p>
              <button
                onClick={signIn}
                data-testid="sign-in-btn"
                className="w-full flex items-center justify-center gap-3 py-3 bg-white border border-stone-200 rounded-xl font-medium hover:bg-stone-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-[#27272A]">Save my thread</span>
              </button>
              <p className="text-xs text-center text-[#A1A1AA]">
                One click. No passwords. No marketing emails.
              </p>
            </div>
          )}

          {/* Trust note */}
          <div className="mt-2 p-3 bg-[#FDFBF7] rounded-xl">
            <p className="text-xs text-[#52525B] text-center">
              {isAuthenticated
                ? "Your data is yours. Export or delete anytime in account settings."
                : "Your threads stay on this device until you choose to save them."
              }
            </p>
          </div>
        </div>
        </div>

        {/* Sticky footer close button — always thumb-reachable on iOS even
            with content scrolled. Duplicates the header X for accessibility
            and reachability. */}
        <div
          className="flex-shrink-0 px-6 py-3 border-t border-stone-100 bg-white"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0))' }}
        >
          <button
            onClick={onClose}
            data-testid="settings-close-footer-button"
            className="w-full py-3 rounded-xl bg-stone-100 hover:bg-stone-200 active:bg-stone-200 text-[#27272A] text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Hook to get settings
export const useAppSettings = () => {
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [celebrationsEnabled, setCelebrationsEnabled] = useState(true);
  const [dailyReminderEnabled, setDailyReminderEnabled] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedAnimations = localStorage.getItem('wwi_animations');
      const storedCelebrations = localStorage.getItem('wwi_celebrations');
      const storedDailyReminder = localStorage.getItem('wwi_daily_reminder');
      
      if (storedAnimations !== null) {
        setAnimationsEnabled(storedAnimations === 'true');
      } else {
        // Respect reduced motion preference
        setAnimationsEnabled(!window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      }
      
      if (storedCelebrations !== null) {
        setCelebrationsEnabled(storedCelebrations === 'true');
      }
      
      if (storedDailyReminder !== null) {
        setDailyReminderEnabled(storedDailyReminder === 'true');
      }
    }
  }, []);

  return { animationsEnabled, celebrationsEnabled, dailyReminderEnabled };
};

export default SettingsPanel;
